import { createEnum } from "@redotech/zod-util/zod-enum";

/**
 * Where an exchange rate came from. Sources are NOT interchangeable — each
 * encodes a different rate (mid-market, fee-inclusive, spread-inclusive) — so
 * `source` is required wherever a rate is read, and never synthesize one source's
 * rate from another's. The grade/fallback policy lives with the FX engine
 * (`@redotech/fx`); this is just the shared vocabulary, kept here so the money
 * type can name a struck value's source without depending on `fx`.
 */
export enum RateSource {
  /**
   * Open Exchange Rates — mid-market, USD-based presentment estimate. The only
   * source whose cross rates are derivable from a base.
   */
  Oxr = "oxr",
  /** Shopify's applied storefront rate (includes Shopify's fee); not derivable from a base. */
  Shopify = "shopify",
  /** Stripe's per-pair settlement rate (includes Stripe's spread); not derivable from a base. */
  Stripe = "stripe",
}

/** Zod schema for {@link RateSource}. */
export const zRateSource = createEnum(RateSource);

const RATE_SOURCES: ReadonlySet<string> = new Set(Object.values(RateSource));

/** Type guard: whether `value` is a valid {@link RateSource} string. */
export function isRateSource(value: unknown): value is RateSource {
  return typeof value === "string" && RATE_SOURCES.has(value);
}
